import { cloneDeep } from 'lodash-es';

import { ICartEntry } from '@rbi-ctg/menu';
import { CartEntryType } from 'utils/cart';

export const mapEntryTypeToOfferType = (entryType: CartEntryType) => {
  let offerType;

  switch (entryType) {
    case CartEntryType.combo:
    case CartEntryType.offerCombo:
      offerType = CartEntryType.offerCombo;
      break;
    case CartEntryType.item:
    case CartEntryType.offerItem:
    default:
      offerType = CartEntryType.offerItem;
      break;
  }

  return offerType;
};

/**
 * Recursively find a match for the swapCartId on the cartEntry or its children.
 *
 * @param cartEntry
 * @param swapCartId
 */
const deepFindCartEntryForSwap = (
  cartEntry: ICartEntry,
  swapCartId: ICartEntry['cartId']
): ICartEntry | undefined => {
  if (cartEntry.cartId === swapCartId) {
    return cartEntry;
  }

  let match: ICartEntry | undefined;
  for (const child of cartEntry.children) {
    match = deepFindCartEntryForSwap(child, swapCartId);
    if (match) {
      break;
    }
  }

  return match;
};

const excludedCartTypes = new Set([
  CartEntryType.offerCombo,
  CartEntryType.offerItem,
  CartEntryType.offerDiscount,
]);

type IEntriesForSwap = {
  rootEntry: ICartEntry | null;
  childEntry: ICartEntry | null;
};

/**
 * Return the first cartEntry that matches the swapCartId.
 * If the match was found within the children of the cartEntry, it returns that reference as well.
 *
 * @param cartEntries
 * @param swapCartId
 */
export const findEntriesForSwap = (
  cartEntries: ICartEntry[],
  swapCartId: ICartEntry['cartId']
): IEntriesForSwap => {
  let entries: IEntriesForSwap = { rootEntry: null, childEntry: null };

  for (const rootEntry of cartEntries) {
    // Swaps can't be applied to OfferCombo / OfferItem
    if (excludedCartTypes.has(rootEntry.type)) {
      continue;
    }

    const childEntry = deepFindCartEntryForSwap(rootEntry, swapCartId);
    if (childEntry) {
      entries = { rootEntry, childEntry };
      break;
    }
  }

  return entries;
};

export type CartEntriesEligibleForSwap = Map<ICartEntry['_id'], ICartEntry>;

/**
 * Return the cartEntries applicable for swaps.
 *
 * @param cartEntries
 */
export const getCartEntriesEligibleForSwap = (
  cartEntries: ICartEntry[],
  cartEntriesIdsToExclude: Set<ICartEntry['cartId']> = new Set()
): CartEntriesEligibleForSwap => {
  const cartEntriesEligibleForSwap: CartEntriesEligibleForSwap = new Map();

  // Recursive loop
  const selectEligibleCartEntries = (
    currentEntries: ICartEntry[],
    eligibleEntries: typeof cartEntriesEligibleForSwap
  ) => {
    for (const cartEntry of currentEntries) {
      // Swaps can't be applied to OfferCombo / OfferItem
      if (excludedCartTypes.has(cartEntry.type)) {
        continue;
      }

      // Skip entries that should be excluded
      if (cartEntriesIdsToExclude.has(cartEntry.cartId)) {
        continue;
      }

      if (cartEntry.children?.length) {
        selectEligibleCartEntries(cartEntry.children, eligibleEntries);
      }

      eligibleEntries.set(cartEntry.sanityId || cartEntry._id, cartEntry);
    }
  };

  selectEligibleCartEntries(cartEntries, cartEntriesEligibleForSwap);
  return cartEntriesEligibleForSwap;
};

/**
 * Traverses cartEntries and replaces the matching item with newEntry
 *
 * @param cartEntries
 * @param swapCartId
 * @param newEntry
 */
export const replaceEntryForSwap = (
  originalCartEntries: ICartEntry[],
  swapCartId: string,
  newEntry: ICartEntry
) => {
  // Avoid mutation on the arguments
  const cartEntries = cloneDeep(originalCartEntries);

  const iterator = (entry: ICartEntry, index: number, array: ICartEntry[]) => {
    // Swaps can't be applied to OfferCombo / OfferItem
    if (excludedCartTypes.has(entry.type)) {
      return false;
    }

    if (entry.cartId === swapCartId) {
      array[index] = newEntry;
      return true;
    }
    return entry.children.length && entry.children.some(iterator);
  };

  cartEntries.some(iterator);

  return [...cartEntries];
};
